import { Injectable } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { IOption } from 'src/app/_modules/shared/_interfaces/option';
import { KycCustomerApiService } from 'src/app/_modules/customers/_services/kyc-customer-api.service';
import {
  KycAccountData,
  LoadRequirement,
  MeterRequest,
  OwnershipStatus,
  ServiceType,
  KycSubServiceType,
} from '../../../_models/kyc-account-data';
import { Title } from '../../../_enums/title.enum';
import { Identification } from '../../../_enums/identification.enum';
import { NatureOfProperty } from '../../../_enums/nature-of-property';
import { TypeOfPremises } from '../../../_enums/type-of-premises';
import { UseOfPremises } from '../../../_enums/use-of-premises';
import { TypeOfProperty } from '../../../_enums/type-of-property';
import { Router } from '@angular/router';
import { Area } from 'src/app/models/area.model';
import { AreaService } from 'src/app/services/area.service';
import { RegionService } from 'src/app/services/region.service';
import { KycRejectionHeader, Rejection } from '../../../_models/kyc-rejection';
import { Gender } from '../../../_enums/gender.enum';
import { BuildingDevelopmentStage } from 'src/app/models/building.model';
import { CustomerService } from 'src/app/services/customer.service';
import { KeyValuePair } from 'src/app/models/keyValueItem.model';
import {
  BuildingType,
  ClientType,
  CustomerType,
  SltStatusDataOut,
} from 'src/app/models/customer.model';
import {
  GeographicalRegionService,
  SubGeographicalRegionService,
  SubGeographicalRegion,
} from 'shared-lib';
import { NATIONALITIES } from '../../../_models/nationalities.data';
import { buildApplianceOptions } from '../../../_models/appliance-options.data';
export type PersonalDataFormSteps = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | number;

@Injectable({
  providedIn: 'root',
})
export class PersonalDataStateService {
  // Personal state
  step: PersonalDataFormSteps = 0; // Set to 0 initialy to hide all forms while loading
  personalDataTitle = 'Customer Service';
  customerId: number | null;
  // Maximum selectable date must be 18 years ago
  maxSelectableDate: Date = new Date(
    new Date().setFullYear(new Date().getFullYear() - 18),
  );

  id: number | null = null;

  // 1. step props
  titles: IOption[] = [
    { displayValue: 'Mr.', value: Title.Mr },
    { displayValue: 'Mrs.', value: Title.Mrs },
    { displayValue: 'Miss.', value: Title.Miss },
    { displayValue: 'Dr.', value: Title.Dr },
  ];

  selectedClientType: IOption | null = null;
  clientTypes: IOption[] = [
    { displayValue: 'Company', value: ClientType.Company },
    {
      displayValue: 'Embassy/Foreign Institution',
      value: ClientType.EmbassyForeignInstitution,
    },
    { displayValue: 'Individual', value: ClientType.Individual },
    { displayValue: 'Government', value: ClientType.Government },
    { displayValue: 'Assembly', value: ClientType.Assembly },
  ];

  selectedCustomerType: IOption | null = null;
  customerTypes: IOption[] = [
    { displayValue: 'Prepaid', value: CustomerType.Prepaid },
    { displayValue: 'Postpaid', value: CustomerType.Postpaid },
  ];

  nationalities: IOption[] = NATIONALITIES;

  public landlordAddressAreaId: number | null;
  public landlordAddressSubGeoRegionId: number | null;
  public selectedNationality: IOption | null =
    this.nationalities.find((el) => el.displayValue === 'Nigeria') || null;
  public selectedTitle: IOption | null = this.titles[0];
  public surname: FormControl = new FormControl('', Validators.required);
  public firstName: FormControl = new FormControl('', Validators.required);
  public phone: FormControl = new FormControl('', Validators.required);
  public dialCode: string | null = null;
  public buildingOwner: FormControl<string | null> = new FormControl('');
  public middleName: FormControl = new FormControl('');
  public email: FormControl = new FormControl('', [
    Validators.required,
    Validators.email,
  ]);
  public dateOfBirth: FormControl<Date | null> = new FormControl(
    null,
    Validators.required,
  );
  public identificationExpiryDate: FormControl<Date | null> = new FormControl(
    null,
  );
  public identificationIssueDate: FormControl<Date | null> = new FormControl(
    null,
  );
  public employerName: FormControl = new FormControl('', Validators.required);
  public houseNo: FormControl = new FormControl('', Validators.required);
  public streetName: FormControl = new FormControl('', Validators.required);
  public landmark: FormControl = new FormControl('');
  public city: FormControl = new FormControl('');
  public area: FormControl = new FormControl('');
  public areaId: number | null = null;
  public selectedRegion: IOption | null;
  public selectedArea: Area | null;

  public subGeoRegion: FormControl = new FormControl('');
  public subGeoRegionId: number | null = null;
  public selectedGeoRegion: IOption | null;
  public selectedSubGeoRegion: SubGeographicalRegion | null;

  genders: IOption[] = [
    { displayValue: 'Male', value: Gender.Male },
    { displayValue: 'Female', value: Gender.Female },
  ];
  selectedGender: IOption | null = this.genders[0];
  public meterRequest: MeterRequest | null;
  // 2. step props
  public accountNumber: FormControl<string | null> = new FormControl('');
  public nin: FormControl<string | null> = new FormControl('', [
    Validators.pattern('[0-9]{11}'),
  ]);
  public meterNumber: FormControl<string | null> = new FormControl('');
  public selectedPremiseType: IOption | null = null;
  public selectedPremiseCategory: IOption | null = null;
  public numberOfPremises: FormControl<string | null> = new FormControl<string>(
    '',
  );
  public individualIdentifications: IOption[] = [
    { displayValue: 'Driver’s License', value: Identification.DriversLicense },
    {
      displayValue: 'International Passport',
      value: Identification.InternationalPassport,
    },
    { displayValue: "Voters' Card", value: Identification.VotersCard },
    { displayValue: 'National ID Card', value: Identification.NationalIdCard },
  ];
  public nonIndividualIdentifications: IOption[] = [
    {
      displayValue: 'Company Registration Number',
      value: Identification.CompanyRegistrationNumber,
    },
    {
      displayValue: 'Company Tax Identification Number',
      value: Identification.CompanyTaxIndentificationNumber,
    },
  ];
  public identificationValue: number | null = null;
  public isLandlord: IOption[] = [
    { displayValue: 'Yes', value: 'true' },
    {
      displayValue: 'No',
      value: 'false',
    },
  ];
  public ownershipStatusOptions: IOption[] = [
    { displayValue: 'Owner', value: OwnershipStatus.Owner },
    { displayValue: 'Tenant', value: OwnershipStatus.Tenant },
  ];
  private _selectedOwnershipStatus: IOption | null = null;
  public get selectedOwnershipStatus(): IOption | null {
    return this._selectedOwnershipStatus;
  }
  public set selectedOwnershipStatus(value: IOption | null) {
    this._selectedOwnershipStatus = value;
    this.buildingOwner.setValidators(
      value?.value === OwnershipStatus.Tenant ? Validators.required : null,
    );
    this.buildingOwner.updateValueAndValidity();
  }
  public selectedIdentificationOfLandlord: IOption | null;
  public personnelIDNumOfLandlord: FormControl<string | null> = new FormControl(
    '',
    Validators.required,
  );
  public supplyHouseNo: FormControl<string | null> = new FormControl(
    '',
    Validators.required,
  );
  public supplyStreetName: FormControl<string | null> = new FormControl(
    '',
    Validators.required,
  );
  public supplyLandmark: FormControl<string | null> = new FormControl('');
  public supplyCity: FormControl<string | null> = new FormControl('');
  public supplyArea: FormControl<string | null> = new FormControl('');
  public geoLatitude: number | null = null;
  public geoLongitude: number | null = null;
  public geoLocation: FormControl<string | null> = new FormControl(
    '',
    Validators.required,
  );
  public bankVerificationNumber: FormControl<string | null> = new FormControl(
    '',
  );
  public supplyAreaId: number | null = null;
  public selectedSupplyRegion: IOption | null;
  public selectedSupplyArea: Area | null;

  public supplySubGeoRegion: FormControl<string | null> = new FormControl('');

  public supplySubGeoRegionId: number | null = null;
  public selectedSupplyGeoRegion: IOption | null;
  public selectedSupplySubGeoRegion: SubGeographicalRegion | null;

  public reviewAppRegion: IOption | null;
  public reviewAppArea: Area | null;
  public reviewAppGeoRegion: IOption | null;
  public reviewAppSubGeoRegion: SubGeographicalRegion | null;

  public typeOfPermises: IOption[] = [
    { displayValue: '2Bed Room Flat', value: TypeOfPremises.TwoBedRoomFlat },
    { displayValue: 'Tenement House', value: TypeOfPremises.TenementHouse },
    { displayValue: '3Bed Room Flat', value: TypeOfPremises.ThreeBedRoomFlat },
    { displayValue: 'Single Room', value: TypeOfPremises.SingleRoom },
    { displayValue: 'Shop/Boys Qt', value: TypeOfPremises.ShopBoysQt },
    { displayValue: 'Block of Flats', value: TypeOfPremises.BlockOfFlats },
    { displayValue: 'Duplex', value: TypeOfPremises.Duplex },
    { displayValue: 'Others', value: TypeOfPremises.Others },
  ];
  public selectedTypeOfPermises: IOption | null;
  public useOfPermises: IOption[] = [
    { displayValue: 'Residential', value: UseOfPremises.Residential },
    { displayValue: 'Commercial', value: UseOfPremises.Commercial },
    { displayValue: 'Special', value: UseOfPremises.Special },
    { displayValue: 'Industrial', value: UseOfPremises.Industrial },
    { displayValue: 'Others', value: UseOfPremises.Others },
  ];
  public selectedUseOfPermises: IOption | null;

  public selectedBuildingDevelopmentStage: IOption | null;
  public buildingDevelopmentStages: IOption[] = [
    { displayValue: 'Completed', value: BuildingDevelopmentStage.Completed },
    {
      displayValue: 'Not Completed',
      value: BuildingDevelopmentStage.NotCompleted,
    },
  ];

  public useofPremiseOptions: IOption[] = [
    {
      value: BuildingType.Residential,
      displayValue: 'Residential',
    },
    {
      value: BuildingType.Commercial,
      displayValue: 'Commercial',
    },
    {
      value: BuildingType.Industrial,
      displayValue: 'Industrial',
    },
    {
      value: BuildingType.Mixed,
      displayValue: 'Mixed',
    },
    {
      value: BuildingType.ReligiousCenter,
      displayValue: 'Religious Center',
    },
  ];
  public selectedUseOfPremise: IOption | null;

  public serviceTypeOptions: IOption[] = [
    { value: ServiceType.KCG, displayValue: 'KCG' },
    { value: ServiceType.NKCG, displayValue: 'NKCG' },
  ];
  public selectedServiceType: IOption | null;

  public subServiceTypeOptions: IOption[] = [
    { value: KycSubServiceType.PublicLoad, displayValue: 'Public Load' },
    {
      value: KycSubServiceType.PointLoadTransformer,
      displayValue: 'Point Load Transformer',
    },
    {
      value: KycSubServiceType.DedicatedFeeder,
      displayValue: 'Dedicated Feeder',
    },
    {
      value: KycSubServiceType.MeterReplacement,
      displayValue: 'Meter Replacement',
    },
    { value: KycSubServiceType.Migration, displayValue: 'Migration' },
    { value: KycSubServiceType.Separation, displayValue: 'Separation' },
    { value: KycSubServiceType.NewService, displayValue: 'New Service' },
    { value: KycSubServiceType.UpgradeToKcg, displayValue: 'Upgrade To KCG' },
  ];
  public selectedSubServiceType: IOption | null;

  public activity: IOption | null;
  public subActivity: IOption | null;
  public sltStatusId: number | null;
  public sltStatus?: SltStatusDataOut;

  // 3. step props

  public previousMeterNumber: FormControl<string | null> = new FormControl('');
  public previousAccountNumber: FormControl<string | null> = new FormControl(
    '',
  );
  public selectedLandlordCheck: IOption | null;

  // 4. step props
  public selectedBuildingOwnerIdentification: IOption | null;
  public personalIDNumber: FormControl<string | null> = new FormControl('');
  // 5. step props

  // Air Conditioner 1HP
  public airConditioner1hpId: number | null;
  public airConditioner1hpNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public airConditioner1hpWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public airConditioner1hpTitle = 'Air Conditioner - 1 HP';
  public airConditioner1hpDefaultWattage = '900';

  // Air Conditioner 1.5HP
  public airConditioner15hpId: number | null;
  public airConditioner15hpNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public airConditioner15hpWattage: FormControl<number | null> =
    new FormControl(0, Validators.min(0));
  public airConditioner15hpTitle = 'Air Conditioner - 1.5 HP';
  public airConditioner15hpDefaultWattage = '1300';

  // Air Conditioner 2HP
  public airConditioner2hpId: number | null;
  public airConditioner2hpNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public airConditioner2hpWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public airConditioner2hpTitle = 'Air Conditioner - 2 HP';
  public airConditioner2hpDefaultWattage = '2000';

  // Air Fryer
  public airFryerId: number | null;
  public airFryerNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public airFryerWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public airFryerTitle = 'Air Fryer';
  public airFryerDefaultWattage = '1500';

  // Blender
  public blenderId: number | null;
  public blenderNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public blenderWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public blenderTitle = 'Blender';
  public blenderDefaultWattage = '500';

  // Ceiling Fan
  public ceilingFanId: number | null;
  public ceilingFanNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public ceilingFanWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public ceilingFanTitle = 'Ceiling Fan';
  public ceilingFanDefaultWattage = '75';

  // Decoder/DSTV/GOTV
  public decoderId: number | null;
  public decoderNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public decoderWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public decoderTitle = 'Decoder / DSTV / GOTV';
  public decoderDefaultWattage = '30';

  // Deep Freezer
  public deepFreezerId: number | null;
  public deepFreezerNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public deepFreezerWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public deepFreezerTitle = 'Deep Freezer';
  public deepFreezerDefaultWattage = '350';

  // Desktop Computer
  public desktopComputerId: number | null;
  public desktopComputerNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public desktopComputerWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public desktopComputerTitle = 'Desktop Computer';
  public desktopComputerDefaultWattage = '300';

  // Electric Cooker / Hot plate
  public electricCookerId: number | null;
  public electricCookerNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public electricCookerWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public electricCookerTitle = 'Electric Cooker / Hot plate';
  public electricCookerDefaultWattage = '2500';

  // Electric iron
  public electricIronId: number | null;
  public electricIronNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public electricIronWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public electricIronTitle = 'Electric Iron';
  public electricIronDefaultWattage = '2000';

  // Electric Kettle
  public electricKettleId: number | null;
  public electricKettleNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public electricKettleWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public electricKettleTitle = 'Electric Kettle';
  public electricKettleDefaultWattage = '2000';

  // Energy Saving Bulb(CFL)
  public energySavingBulbId: number | null;
  public energySavingBulbNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public energySavingBulbWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public energySavingBulbTitle = 'Energy Saving Bulb (CFL)';
  public energySavingBulbDefaultWattage = '20';

  // Flourescent Tube
  public flourescentTubeId: number | null;
  public flourescentTubeNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public flourescentTubeWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public flourescentTubeTitle = 'Flourescent Tube';
  public flourescentTubeDefaultWattage = '40';

  // Home Theatre / Sound System
  public homeTheatreId: number | null;
  public homeTheatreNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public homeTheatreWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public homeTheatreTitle = 'Home Theatre / Sound System';
  public homeTheatreDefaultWattage = '300';

  // Incandescent Bulb
  public incandescentBulbId: number | null;
  public incandescentBulbNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public incandescentBulbWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public incandescentBulbTitle = 'Incandescent Bulb';
  public incandescentBulbDefaultWattage = '60';

  // Laptop
  public laptopId: number | null;
  public laptopNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public laptopWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public laptopTitle = 'Laptop';
  public laptopDefaultWattage = '75';

  // LED Bulb
  public ledBulbId: number | null;
  public ledBulbNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public ledBulbWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public ledBulbTitle = 'LED Bulb';
  public ledBulbDefaultWattage = '10';

  // Microwave Oven
  public microwaveOvenId: number | null;
  public microwaveOvenNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public microwaveOvenWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public microwaveOvenTitle = 'Microwave Oven';
  public microwaveOvenDefaultWattage = '1200';

  // Printer
  public printerId: number | null;
  public printerNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public printerWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public printerTitle = 'Printer';
  public printerDefaultWattage = '500';

  // Refrigerator
  public refrigeratorId: number | null;
  public refrigeratorNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public refrigeratorWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public refrigeratorTitle = 'Refrigerator';
  public refrigeratorDefaultWattage = '250';

  // Standing Fan
  public standingFanId: number | null;
  public standingFanNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public standingFanWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public standingFanTitle = 'Standing Fan';
  public standingFanDefaultWattage = '100';

  // Television
  public televisionId: number | null;
  public televisionNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public televisionWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public televisionTitle = 'Television';
  public televisionDefaultWattage = '150';

  // Toaster
  public toasterId: number | null;
  public toasterNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public toasterWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public toasterTitle = 'Toaster';
  public toasterDefaultWattage = '1000';

  // Vacuum Cleaner
  public vacuumCleanerId: number | null;
  public vacuumCleanerNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public vacuumCleanerWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public vacuumCleanerTitle = 'Vacuum Cleaner';
  public vacuumCleanerDefaultWattage = '1200';

  // Washing Machine
  public washingMachineId: number | null;
  public washingMachineNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public washingMachineWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public washingMachineTitle = 'Washing Machine';
  public washingMachineDefaultWattage = '1000';

  // Water Heater
  public waterHeaterId: number | null;
  public waterHeaterNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public waterHeaterWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public waterHeaterTitle = 'Water Heater';
  public waterHeaterDefaultWattage = '2500';

  // Water Pump - 0.5HP
  public waterPump05hpId: number | null;
  public waterPump05hpNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public waterPump05hpWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public waterPump05hpTitle = 'Water Pump - 0.5 HP';
  public waterPump05hpDefaultWattage = '500';

  // Water Pump - 1HP
  public waterPump1hpId: number | null;
  public waterPump1hpNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public waterPump1hpWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public waterPump1hpTitle = 'Water Pump - 1 HP';
  public waterPump1hpDefaultWattage = '1000';

  // Other
  public otherId: number | null;
  public otherNumber: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public otherWattage: FormControl<number | null> = new FormControl(
    0,
    Validators.min(0),
  );
  public otherTitle = 'Other';

  public applianceOptions: string[] = buildApplianceOptions(this);

  // 6. step props
  public natureOfProperty: IOption[] = [
    { displayValue: 'Old House', value: NatureOfProperty.OldHouse },
    { displayValue: 'New House', value: NatureOfProperty.NewHouse },
  ];
  public selectedNatureOfProperty: IOption | null;
  public typeOfProperty: IOption[] = [
    { displayValue: 'Apartment', value: TypeOfProperty.Apartment },
    { displayValue: 'Terrace', value: TypeOfProperty.Terrace },
    { displayValue: 'Pump House', value: TypeOfProperty.PumpHouse },
    { displayValue: 'Single House', value: TypeOfProperty.SingleHouse },
    { displayValue: 'Others', value: TypeOfProperty.Others },
  ];
  public selectedtypeOfProperty: IOption | null;
  public propertyHavePlanningPermission: boolean = true;
  public propertyHaveMeter: boolean = true;
  public meterNumber6: FormControl<string | null> = new FormControl('');
  public customerAccountNumberForPremises: FormControl<string | null> =
    new FormControl('');
  public estMaxLoad: FormControl<number | null> = new FormControl(
    null,
    Validators.pattern('([0-9]*[.])?[0-9]+'),
  );
  public requestedVoltage: FormControl<number | null> = new FormControl(
    null,
    Validators.pattern('([0-9]*[.])?[0-9]+'),
  );
  public engineerName: FormControl<string | null> = new FormControl('');
  public engineerLastName: FormControl<string | null> = new FormControl('');
  public engineerNameDesktop: FormControl<string | null> = new FormControl('');

  // 7. step props
  public engineerHouseNo: FormControl<string | null> = new FormControl('');
  public engineerStreet: FormControl<string | null> = new FormControl('');
  public engineerLandmark: FormControl<string | null> = new FormControl('');
  public engineerCity: FormControl<string | null> = new FormControl('');
  public engineerArea: FormControl<string | null> = new FormControl('');
  public licenseNumber: FormControl<string | null> = new FormControl('');
  public category: FormControl<string | null> = new FormControl('');
  public seventhStepDate: FormControl<Date | null> = new FormControl(null);
  public engineerMeterNumber: FormControl<string | null> = new FormControl('');
  public customerAccountNumber: FormControl<string | null> = new FormControl(
    '',
  );

  // 8. step props
  public customersSignitureUrl: string | null;
  public landlordsSignitureUrl: string | null;
  public electricalContractorsSignitureUrl: string | null;
  public applicantsSignitureUrl: string | null;
  public passportUrl: string | null;
  public customersSignitureName: string;
  public landlordsSignitureName: string;
  public electricalContractorsSignitureName: string;
  public applicantsSignitureName: string;
  public passportName: string;
  public isAgreed: boolean = true;
  public rejection: boolean = true;
  public activityList: KeyValuePair[] = [];
  public regions: IOption[] = [];
  public areas: Area[] = [];
  public shownAreas: Area[] = [];
  public rejections?: Rejection[];
  public isNavigatingToMeterRequest: boolean = false;
  public hasCimIntegrationEnabled: boolean = false;
  public geographicalRegions: IOption[] = [];
  public subGeoRegions: SubGeographicalRegion[] = [];
  public shownSubGeoRegions: SubGeographicalRegion[] = [];

  public proofOfOwnershipUrl: string | null;
  public letterOfConsentUrl: string | null;
  public tenancyAgreementUrl: string | null;
  public letterOfAuthorizationUrl: string | null;

  public proofOfOwnershipName: string;
  public letterOfConsentName: string;
  public tenancyAgreementName: string;
  public letterOfAuthorizationName: string;

  constructor(
    private kycCustomerApiService: KycCustomerApiService,
    private toastrService: ToastrService,
    private router: Router,
    private areaService: AreaService,
    private regionService: RegionService,
    private customerService: CustomerService,
    private geographicalRegionService: GeographicalRegionService,
    private subGeographicalRegionService: SubGeographicalRegionService,
  ) {}

  saveAndContinue() {
    let request: KycAccountData = this.setKycAccountDataForServer();

    this.kycCustomerApiService.saveCustomer(request).subscribe({
      // Test object for backend, switch with real object
      next: this.handleSaveKycAccountData.bind(this),
      error: this.handleError.bind(this),
    });
  }

  rejectionSaveAndContinue() {
    let request: KycAccountData = this.setKycAccountDataForServer();
    this.kycCustomerApiService.saveCustomer(request).subscribe({
      // Test object for beckend, switch with real object
      next: this.handleRejectionSaveKycAccountData.bind(this),
      error: this.handleError.bind(this),
    });
  }

  rejectionContinue() {
    let request: KycAccountData = this.setKycAccountDataForServer();
    this.kycCustomerApiService.saveCustomer(request).subscribe({
      // Test object for beckend, switch with real object
      next: this.handleRejectionContinueKycAccountData.bind(this),
      error: this.handleError.bind(this),
    });

    // TODO: Only for development until server is finished
    // this.handleContinueKycAccountData(null);
  }

  continue() {
    let request: KycAccountData = this.setKycAccountDataForServer();
    this.kycCustomerApiService.saveCustomer(request).subscribe({
      // Test object for beckend, switch with real object
      next: this.handleContinueKycAccountData.bind(this),
      error: this.handleError.bind(this),
    });

    // TODO: Only for development until server is finished
    // this.handleContinueKycAccountData(null);
  }

  saveLoadEquipments() {
    let request: KycAccountData = this.setKycAccountDataForServer();

    this.kycCustomerApiService.saveCustomer(request).subscribe({
      next: (responsy: any) => {
        this.toastrService.success('Load Equipments saved successfully');
      },
    });
  }

  goBack() {
    this.step = this.step > 1 ? this.step - 1 : 1;
    this.setPersonalDataTitle();
  }

  setPersonalDataTitle() {
    switch (this.step) {
      case 1:
        this.personalDataTitle = 'Customers Data';
        break;
      case 2:
        this.personalDataTitle = 'Service Point Data';
        break;
      case 3:
        this.personalDataTitle =
          'Declaration: Load Requirement for the premises';
        break;
      case 4:
        this.personalDataTitle = 'Upload File';
        break;
      case 5:
        this.personalDataTitle =
          'Declaration: Load Requirement for the premises';
        break;
      case 6:
        this.personalDataTitle = 'Personal Data - V';
        break;
      case 7:
        this.personalDataTitle = 'Personal Data - VI';
        break;
      case 8:
        this.personalDataTitle = 'Upload File';
        break;
      default:
        this.personalDataTitle = 'Personal Data';
    }
  }

  public resetState() {
    // Personal state
    this.step = 1;
    this.setPersonalDataTitle();
    this.customerId = null;
    this.meterRequest = null;
    // 1. step props
    this.selectedTitle = null;
    this.surname.setValue('');
    this.firstName.setValue('');
    this.phone.setValue('');
    this.dialCode = null;
    this.email.setValue('');
    this.dateOfBirth.setValue(null);
    this.identificationIssueDate.setValue(null);
    this.identificationExpiryDate.setValue(null);
    this.employerName.setValue('');
    this.houseNo.setValue('');
    this.streetName.setValue('');
    this.landmark.setValue('');
    this.city.setValue('');
    this.area.setValue('');
    this.areaId = null;
    this.selectedRegion = null;
    this.selectedArea = null;

    this.subGeoRegion.setValue('');
    this.subGeoRegionId = null;
    this.selectedGeoRegion = null;
    this.selectedSubGeoRegion = null;

    // 2. step props
    this.meterNumber.setValue('');
    this.numberOfPremises.setValue('');
    this.selectedIdentificationOfLandlord = null;
    this.selectedServiceType = null;
    this.selectedSubServiceType = null;
    this.personnelIDNumOfLandlord.setValue('');
    this.supplyHouseNo.setValue('');
    this.supplyStreetName.setValue('');
    this.supplyLandmark.setValue('');
    this.geoLatitude = null;
    this.geoLongitude = null;
    this.geoLocation.setValue('');
    this.supplyCity.setValue('');

    this.supplyArea.setValue('');
    this.selectedSupplyArea = null;
    this.reviewAppArea = null;
    this.supplyAreaId = null;
    this.reviewAppRegion = null;
    this.selectedSupplyRegion = null;

    this.supplySubGeoRegion.setValue('');
    this.selectedSubGeoRegion = null;
    this.reviewAppSubGeoRegion = null;
    this.supplySubGeoRegionId = null;
    this.reviewAppGeoRegion = null;
    this.selectedSupplyGeoRegion = null;

    this.selectedTypeOfPermises = null;
    this.selectedUseOfPermises = null;
    this.bankVerificationNumber.setValue('');
    this.activity = null;
    this.subActivity = null;

    this.selectedLandlordCheck = null;

    this.buildingOwner.setValue('');

    // 4. step props
    this.personalIDNumber.setValue('');

    // 5. step props
    this.airConditioner1hpNumber.setValue(0);
    this.airConditioner1hpWattage.setValue(0);
    this.airConditioner15hpNumber.setValue(0);
    this.airConditioner15hpWattage.setValue(0);
    this.airConditioner2hpNumber.setValue(0);
    this.airConditioner2hpWattage.setValue(0);
    this.airFryerNumber.setValue(0);
    this.airFryerWattage.setValue(0);
    this.blenderNumber.setValue(0);
    this.blenderWattage.setValue(0);
    this.ceilingFanNumber.setValue(0);
    this.ceilingFanWattage.setValue(0);
    this.decoderNumber.setValue(0);
    this.decoderWattage.setValue(0);
    this.deepFreezerNumber.setValue(0);
    this.deepFreezerWattage.setValue(0);
    this.desktopComputerNumber.setValue(0);
    this.desktopComputerWattage.setValue(0);
    this.electricCookerNumber.setValue(0);
    this.electricCookerWattage.setValue(0);
    this.electricIronNumber.setValue(0);
    this.electricIronWattage.setValue(0);
    this.electricKettleNumber.setValue(0);
    this.electricKettleWattage.setValue(0);
    this.energySavingBulbNumber.setValue(0);
    this.energySavingBulbWattage.setValue(0);
    this.flourescentTubeNumber.setValue(0);
    this.flourescentTubeWattage.setValue(0);
    this.homeTheatreNumber.setValue(0);
    this.homeTheatreWattage.setValue(0);
    this.incandescentBulbNumber.setValue(0);
    this.incandescentBulbWattage.setValue(0);
    this.laptopNumber.setValue(0);
    this.laptopWattage.setValue(0);
    this.ledBulbNumber.setValue(0);
    this.ledBulbWattage.setValue(0);
    this.microwaveOvenNumber.setValue(0);
    this.microwaveOvenWattage.setValue(0);
    this.printerNumber.setValue(0);
    this.printerWattage.setValue(0);
    this.refrigeratorNumber.setValue(0);
    this.refrigeratorWattage.setValue(0);
    this.standingFanNumber.setValue(0);
    this.standingFanWattage.setValue(0);
    this.televisionNumber.setValue(0);
    this.televisionWattage.setValue(0);
    this.toasterNumber.setValue(0);
    this.toasterWattage.setValue(0);
    this.vacuumCleanerNumber.setValue(0);
    this.vacuumCleanerWattage.setValue(0);
    this.washingMachineNumber.setValue(0);
    this.washingMachineWattage.setValue(0);
    this.waterHeaterNumber.setValue(0);
    this.waterHeaterWattage.setValue(0);
    this.waterPump05hpNumber.setValue(0);
    this.waterPump05hpWattage.setValue(0);
    this.waterPump1hpNumber.setValue(0);
    this.waterPump1hpWattage.setValue(0);
    this.otherNumber.setValue(0);
    this.otherWattage.setValue(0);

    // 6. step props
    this.selectedNatureOfProperty = null;
    this.selectedtypeOfProperty = null;
    this.propertyHavePlanningPermission = true;
    this.propertyHaveMeter = true;
    this.meterNumber6.setValue('');
    this.customerAccountNumberForPremises.setValue('');
    this.estMaxLoad.setValue(null);
    this.requestedVoltage.setValue(null);
    this.engineerName.setValue('');
    this.engineerLastName.setValue('');
    this.engineerNameDesktop.setValue('');

    // 7. step props
    this.engineerHouseNo.setValue('');
    this.engineerStreet.setValue('');
    this.engineerLandmark.setValue('');
    this.engineerCity.setValue('');
    this.engineerArea.setValue('');
    this.licenseNumber.setValue('');
    this.category.setValue('');
    this.seventhStepDate.setValue(null);
    this.engineerMeterNumber.setValue('');
    this.customerAccountNumber.setValue('');

    // 8. step props
    this.customersSignitureUrl = null;
    this.landlordsSignitureUrl = null;
    this.electricalContractorsSignitureUrl = null;
    this.applicantsSignitureUrl = null;
    this.passportUrl = null;
    this.isAgreed = true;

    this.proofOfOwnershipUrl = null;
    this.letterOfConsentUrl = null;
    this.tenancyAgreementUrl = null;
    this.letterOfAuthorizationUrl = null;
  }

  public setCustomerType(customerType?: CustomerType) {
    return this.customerTypes.find((type) => type.value === customerType)
      ?.displayValue;
  }

  public setClientType(clientType?: ClientType) {
    return this.clientTypes.find((type) => type.value === clientType)
      ?.displayValue;
  }

  public handleContinueKycAccountData(response: any) {
    // TODO Change this part for next delpoyment, handle other 4 pages also
    this.setServiceStateFromServer(new KycAccountData(response.data));
    this.step = this.step < 10 ? this.step + 1 : 9;

    if (this.step > 8)
      this.router.navigate(['/customers-app/account-information']);
    this.setPersonalDataTitle();
  }

  public handleRejectionContinueKycAccountData(response: any) {
    const nextStep = this.getNextRejectionFormStep(this.step);
    if (nextStep !== null) {
      this.step = nextStep;
      this.setPersonalDataTitle();
    } else {
      this.router.navigate(['/customers-app/profile-incomplete']);
    }
  }

  public handleRejectionSaveKycAccountData(response: any) {
    const nextStep = this.getNextRejectionFormStep(this.step);
    if (nextStep !== null) {
      this.step = nextStep;
      this.setPersonalDataTitle();
    } else {
      this.kycCustomerApiService.setAccountKYCStep(13).subscribe({
        next: this.handleRejectionsetAccountKYCStep.bind(this),
        error: this.handleError.bind(this),
      });
    }
  }

  rejectionSaveAndGoToMeterRequest() {
    let request: KycAccountData = this.setKycAccountDataForServer();
    this.kycCustomerApiService.saveCustomer(request).subscribe({
      next: () =>
        this.kycCustomerApiService.setAccountKYCStep(13).subscribe({
          next: () => {
            this.isNavigatingToMeterRequest = true;
            this.step = 4;
            this.router.navigate(['/customers-app']);
          },
          error: this.handleError.bind(this),
        }),
      error: this.handleError.bind(this),
    });
  }

  handleRejectionsetAccountKYCStep() {
    this.router.navigate(['/customers-app/profile-incomplete']);
  }

  private readonly rejectionStep1Fields: string[] = [
    'FirstName',
    'Surname',
    'OwnershipStatus',
    'BuildingOwnersName',
    'ServiceType',
    'SubServiceType',
    'CustomerType',
    'ClientType',
    'IdentificationType',
    'PersonalIdNumber',
  ];

  private readonly rejectionStep2Fields: string[] = [
    'Region',
    'Area',
    'UseOfPremise',
    'Geolocation',
  ];

  private hasStep1Rejections(rejections: any[]): boolean {
    return rejections.some((r) =>
      r.rejectedFields?.some((f: string) =>
        this.rejectionStep1Fields.includes(f),
      ),
    );
  }

  private hasStep2Rejections(rejections: any[]): boolean {
    return rejections.some((r) =>
      r.rejectedFields?.some((f: string) =>
        this.rejectionStep2Fields.includes(f),
      ),
    );
  }

  private hasMeterRequestRejections(rejections: any[]): boolean {
    return rejections.some(
      (r) =>
        r.rejectedPage?.toString() ===
        KycRejectionHeader[KycRejectionHeader.MeterRequest],
    );
  }

  private getNextRejectionFormStep(afterFormStep: number): number | null {
    if (!this.rejections || this.rejections.length === 0) return null;
    const steps: Array<{ formStep: number; check: (r: any[]) => boolean }> = [
      { formStep: 1, check: (r) => this.hasStep1Rejections(r) },
      { formStep: 2, check: (r) => this.hasStep2Rejections(r) },
      { formStep: 4, check: (r) => this.hasMeterRequestRejections(r) },
      {
        formStep: 5,
        check: (r) => r.some((x: any) => x.rejectedImages?.length > 0),
      },
    ];
    for (const entry of steps) {
      if (entry.formStep > afterFormStep && entry.check(this.rejections)) {
        return entry.formStep;
      }
    }
    return null;
  }

  private getFirstRejectionFormStep(rejections: any[]): number | null {
    if (!rejections || rejections.length === 0) return null;
    if (this.hasStep1Rejections(rejections)) return 1;
    if (this.hasStep2Rejections(rejections)) return 2;
    if (this.hasMeterRequestRejections(rejections)) return 4;
    if (rejections.some((r) => r.rejectedImages?.length > 0)) return 5;
    return null;
  }

  public handleSaveKycAccountData(response: any) {
    this.setServiceStateFromServer(new KycAccountData(response.data));
    this.resetState();
    this.router.navigate(['/customers-app/profile-incomplete']);
  }

  public handleGetKycAccountData(event: any) {
    this.setServiceStateFromServer(new KycAccountData(event.data));
    if (this.hasCimIntegrationEnabled && this.subGeoRegions?.length > 0 && this.geographicalRegions?.length > 0) {
      this.mapSelectedGeoAndSubGeoRegions();
    }

    if (!this.hasCimIntegrationEnabled && this.areas?.length > 0 && this.regions?.length > 0) {
      this.mapSelectedRegionAndAreas();
    }
  }

  public handleError(event: any) {
    this.toastrService.error('An error occured');
  }

  setTitle(num: number | null | undefined): IOption {
    return this.titles.find((el) => el.value === num) ?? this.titles[0];
  }

  setIdentification(
    num: number | null | undefined,
    clientType: ClientType | null | undefined,
  ): IOption | null {
    if (clientType === ClientType.Individual)
      return (
        this.individualIdentifications.find((el) => el.value === num) ?? null
      );
    return (
      this.nonIndividualIdentifications.find((el) => el.value === num) ?? null
    );
  }

  setServiceType(displayValue: string | null | undefined): IOption | null {
    return (
      this.serviceTypeOptions.find((el) => el.displayValue === displayValue) ??
      null
    );
  }

  setSubServiceType(displayValue: string | null | undefined): IOption | null {
    return (
      this.subServiceTypeOptions.find(
        (el) =>
          el.displayValue.replace(/\s/g, '').toLowerCase() ===
          displayValue?.replace(/\s/g, '').toLowerCase(),
      ) ?? null
    );
  }

  setGender(gender: string | null | undefined): IOption | null {
    return this.genders.find((el) => el.displayValue === gender) ?? null;
  }

  setNationality(nationality: string | null | undefined): IOption | null {
    return (
      this.nationalities.find((el) => el.displayValue === nationality) ?? null
    );
  }

  setOwnershipStatus(
    ownershipStatus: string | null | undefined,
  ): IOption | null {
    return (
      this.ownershipStatusOptions.find(
        (el) => el.displayValue === ownershipStatus,
      ) ?? null
    );
  }

  setIdentificationOfBuildingOwner(
    num: number | null | undefined,
  ): IOption | null {
    return (
      this.individualIdentifications.find((el) => el.value === num) ?? null
    );
  }

  setNatureOfProperty(num: number | null | undefined): IOption | null {
    return this.natureOfProperty.find((el) => el.value === num) ?? null;
  }

  setTypeOfProperty(num: number | null | undefined): IOption | null {
    return this.typeOfProperty.find((el) => el.value === num) ?? null;
  }

  setUseOfPermises(num: number | null | undefined): IOption | null {
    return this.useOfPermises.find((el) => el.value === num) ?? null;
  }

  setTypeOfPermises(num: number | null | undefined): IOption | null {
    return this.typeOfPermises.find((el) => el.value === num) ?? null;
  }

  setBuildingDevelopmentStage(
    displayValue: string | null | undefined,
  ): IOption | null {
    return (
      this.buildingDevelopmentStages.find(
        (el) => el.displayValue === displayValue,
      ) ?? null
    );
  }

  setUseOfPremise(displayValue: string | null | undefined): IOption | null {
    return (
      this.useofPremiseOptions.find((el) => el.displayValue === displayValue) ??
      null
    );
  }

  setFifthStepFromServer(requirements: LoadRequirement[]) {
    for (let index = 0; index < requirements?.length; index++) {
      const element: LoadRequirement = requirements[index];
      switch (element.name) {
        case this.airConditioner1hpTitle:
          this.airConditioner1hpId = element.id;
          this.airConditioner1hpNumber.setValue(element.number ?? 0);
          this.airConditioner1hpWattage.setValue(element.wattage ?? 0);
          break;
        case this.airConditioner15hpTitle:
          this.airConditioner15hpId = element.id;
          this.airConditioner15hpNumber.setValue(element.number ?? 0);
          this.airConditioner15hpWattage.setValue(element.wattage ?? 0);
          break;
        case this.airConditioner2hpTitle:
          this.airConditioner2hpId = element.id;
          this.airConditioner2hpNumber.setValue(element.number ?? 0);
          this.airConditioner2hpWattage.setValue(element.wattage ?? 0);
          break;
        case this.airFryerTitle:
          this.airFryerId = element.id;
          this.airFryerNumber.setValue(element.number ?? 0);
          this.airFryerWattage.setValue(element.wattage ?? 0);
          break;
        case this.blenderTitle:
          this.blenderId = element.id;
          this.blenderNumber.setValue(element.number ?? 0);
          this.blenderWattage.setValue(element.wattage ?? 0);
          break;
        case this.ceilingFanTitle:
          this.ceilingFanId = element.id;
          this.ceilingFanNumber.setValue(element.number ?? 0);
          this.ceilingFanWattage.setValue(element.wattage ?? 0);
          break;
        case this.decoderTitle:
          this.decoderId = element.id;
          this.decoderNumber.setValue(element.number ?? 0);
          this.decoderWattage.setValue(element.wattage ?? 0);
          break;
        case this.deepFreezerTitle:
          this.deepFreezerId = element.id;
          this.deepFreezerNumber.setValue(element.number ?? 0);
          this.deepFreezerWattage.setValue(element.wattage ?? 0);
          break;
        case this.desktopComputerTitle:
          this.desktopComputerId = element.id;
          this.desktopComputerNumber.setValue(element.number ?? 0);
          this.desktopComputerWattage.setValue(element.wattage ?? 0);
          break;
        case this.electricCookerTitle:
          this.electricCookerId = element.id;
          this.electricCookerNumber.setValue(element.number ?? 0);
          this.electricCookerWattage.setValue(element.wattage ?? 0);
          break;
        case this.electricIronTitle:
          this.electricIronId = element.id;
          this.electricIronNumber.setValue(element.number ?? 0);
          this.electricIronWattage.setValue(element.wattage ?? 0);
          break;
        case this.electricKettleTitle:
          this.electricKettleId = element.id;
          this.electricKettleNumber.setValue(element.number ?? 0);
          this.electricKettleWattage.setValue(element.wattage ?? 0);
          break;
        case this.energySavingBulbTitle:
          this.energySavingBulbId = element.id;
          this.energySavingBulbNumber.setValue(element.number ?? 0);
          this.energySavingBulbWattage.setValue(element.wattage ?? 0);
          break;
        case this.flourescentTubeTitle:
          this.flourescentTubeId = element.id;
          this.flourescentTubeNumber.setValue(element.number ?? 0);
          this.flourescentTubeWattage.setValue(element.wattage ?? 0);
          break;
        case this.homeTheatreTitle:
          this.homeTheatreId = element.id;
          this.homeTheatreNumber.setValue(element.number ?? 0);
          this.homeTheatreWattage.setValue(element.wattage ?? 0);
          break;
        case this.incandescentBulbTitle:
          this.incandescentBulbId = element.id;
          this.incandescentBulbNumber.setValue(element.number ?? 0);
          this.incandescentBulbWattage.setValue(element.wattage ?? 0);
          break;
        case this.laptopTitle:
          this.laptopId = element.id;
          this.laptopNumber.setValue(element.number ?? 0);
          this.laptopWattage.setValue(element.wattage ?? 0);
          break;
        case this.ledBulbTitle:
          this.ledBulbId = element.id;
          this.ledBulbNumber.setValue(element.number ?? 0);
          this.ledBulbWattage.setValue(element.wattage ?? 0);
          break;
        case this.microwaveOvenTitle:
          this.microwaveOvenId = element.id;
          this.microwaveOvenNumber.setValue(element.number ?? 0);
          this.microwaveOvenWattage.setValue(element.wattage ?? 0);
          break;
        case this.printerTitle:
          this.printerId = element.id;
          this.printerNumber.setValue(element.number ?? 0);
          this.printerWattage.setValue(element.wattage ?? 0);
          break;
        case this.refrigeratorTitle:
          this.refrigeratorId = element.id;
          this.refrigeratorNumber.setValue(element.number ?? 0);
          this.refrigeratorWattage.setValue(element.wattage ?? 0);
          break;
        case this.standingFanTitle:
          this.standingFanId = element.id;
          this.standingFanNumber.setValue(element.number ?? 0);
          this.standingFanWattage.setValue(element.wattage ?? 0);
          break;
        case this.televisionTitle:
          this.televisionId = element.id;
          this.televisionNumber.setValue(element.number ?? 0);
          this.televisionWattage.setValue(element.wattage ?? 0);
          break;
        case this.toasterTitle:
          this.toasterId = element.id;
          this.toasterNumber.setValue(element.number ?? 0);
          this.toasterWattage.setValue(element.wattage ?? 0);
          break;
        case this.vacuumCleanerTitle:
          this.vacuumCleanerId = element.id;
          this.vacuumCleanerNumber.setValue(element.number ?? 0);
          this.vacuumCleanerWattage.setValue(element.wattage ?? 0);
          break;
        case this.washingMachineTitle:
          this.washingMachineId = element.id;
          this.washingMachineNumber.setValue(element.number ?? 0);
          this.washingMachineWattage.setValue(element.wattage ?? 0);
          break;
        case this.waterHeaterTitle:
          this.waterHeaterId = element.id;
          this.waterHeaterNumber.setValue(element.number ?? 0);
          this.waterHeaterWattage.setValue(element.wattage ?? 0);
          break;
        case this.waterPump05hpTitle:
          this.waterPump05hpId = element.id;
          this.waterPump05hpNumber.setValue(element.number ?? 0);
          this.waterPump05hpWattage.setValue(element.wattage ?? 0);
          break;
        case this.waterPump1hpTitle:
          this.waterPump1hpId = element.id;
          this.waterPump1hpNumber.setValue(element.number ?? 0);
          this.waterPump1hpWattage.setValue(element.wattage ?? 0);
          break;
        case this.otherTitle:
          this.otherId = element.id;
          this.otherNumber.setValue(element.number ?? 0);
          this.otherWattage.setValue(element.wattage ?? 0);
          break;
      }
    }
  }

  setFifthStepForServer(): LoadRequirement[] {
    const loadRequirements: LoadRequirement[] = [];

    if (
      this.airConditioner1hpNumber.value ||
      this.airConditioner1hpWattage.value
    ) {
      loadRequirements.push({
        id: this.airConditioner1hpId,
        name: this.airConditioner1hpTitle,
        number: isNaN(Number(this.airConditioner1hpNumber?.value))
          ? null
          : Number(this.airConditioner1hpNumber?.value),
        wattage: isNaN(Number(this.airConditioner1hpWattage?.value))
          ? null
          : Number(this.airConditioner1hpWattage?.value),
      });
    }

    if (
      this.airConditioner15hpNumber.value ||
      this.airConditioner15hpWattage.value
    ) {
      loadRequirements.push({
        id: this.airConditioner15hpId,
        name: this.airConditioner15hpTitle,
        number: isNaN(Number(this.airConditioner15hpNumber?.value))
          ? null
          : Number(this.airConditioner15hpNumber?.value),
        wattage: isNaN(Number(this.airConditioner15hpWattage?.value))
          ? null
          : Number(this.airConditioner15hpWattage?.value),
      });
    }

    if (
      this.airConditioner2hpNumber.value ||
      this.airConditioner2hpWattage.value
    ) {
      loadRequirements.push({
        id: this.airConditioner2hpId,
        name: this.airConditioner2hpTitle,
        number: isNaN(Number(this.airConditioner2hpNumber?.value))
          ? null
          : Number(this.airConditioner2hpNumber?.value),
        wattage: isNaN(Number(this.airConditioner2hpWattage?.value))
          ? null
          : Number(this.airConditioner2hpWattage?.value),
      });
    }

    if (this.airFryerNumber.value || this.airFryerWattage.value) {
      loadRequirements.push({
        id: this.airFryerId,
        name: this.airFryerTitle,
        number: isNaN(Number(this.airFryerNumber?.value))
          ? null
          : Number(this.airFryerNumber?.value),
        wattage: isNaN(Number(this.airFryerWattage?.value))
          ? null
          : Number(this.airFryerWattage?.value),
      });
    }

    if (this.blenderNumber.value || this.blenderWattage.value) {
      loadRequirements.push({
        id: this.blenderId,
        name: this.blenderTitle,
        number: isNaN(Number(this.blenderNumber?.value))
          ? null
          : Number(this.blenderNumber?.value),
        wattage: isNaN(Number(this.blenderWattage?.value))
          ? null
          : Number(this.blenderWattage?.value),
      });
    }

    if (this.ceilingFanNumber.value || this.ceilingFanWattage.value) {
      loadRequirements.push({
        id: this.ceilingFanId,
        name: this.ceilingFanTitle,
        number: isNaN(Number(this.ceilingFanNumber?.value))
          ? null
          : Number(this.ceilingFanNumber?.value),
        wattage: isNaN(Number(this.ceilingFanWattage?.value))
          ? null
          : Number(this.ceilingFanWattage?.value),
      });
    }

    if (this.decoderNumber.value || this.decoderWattage.value) {
      loadRequirements.push({
        id: this.decoderId,
        name: this.decoderTitle,
        number: isNaN(Number(this.decoderNumber?.value))
          ? null
          : Number(this.decoderNumber?.value),
        wattage: isNaN(Number(this.decoderWattage?.value))
          ? null
          : Number(this.decoderWattage?.value),
      });
    }

    if (this.deepFreezerNumber.value || this.deepFreezerWattage.value) {
      loadRequirements.push({
        id: this.deepFreezerId,
        name: this.deepFreezerTitle,
        number: isNaN(Number(this.deepFreezerNumber?.value))
          ? null
          : Number(this.deepFreezerNumber?.value),
        wattage: isNaN(Number(this.deepFreezerWattage?.value))
          ? null
          : Number(this.deepFreezerWattage?.value),
      });
    }

    if (this.desktopComputerNumber.value || this.desktopComputerWattage.value) {
      loadRequirements.push({
        id: this.desktopComputerId,
        name: this.desktopComputerTitle,
        number: isNaN(Number(this.desktopComputerNumber?.value))
          ? null
          : Number(this.desktopComputerNumber?.value),
        wattage: isNaN(Number(this.desktopComputerWattage?.value))
          ? null
          : Number(this.desktopComputerWattage?.value),
      });
    }

    if (this.electricCookerNumber.value || this.electricCookerWattage.value) {
      loadRequirements.push({
        id: this.electricCookerId,
        name: this.electricCookerTitle,
        number: isNaN(Number(this.electricCookerNumber?.value))
          ? null
          : Number(this.electricCookerNumber?.value),
        wattage: isNaN(Number(this.electricCookerWattage?.value))
          ? null
          : Number(this.electricCookerWattage?.value),
      });
    }

    if (this.electricIronNumber.value || this.electricIronWattage.value) {
      loadRequirements.push({
        id: this.electricIronId,
        name: this.electricIronTitle,
        number: isNaN(Number(this.electricIronNumber?.value))
          ? null
          : Number(this.electricIronNumber?.value),
        wattage: isNaN(Number(this.electricIronWattage?.value))
          ? null
          : Number(this.electricIronWattage?.value),
      });
    }

    if (this.electricKettleNumber.value || this.electricKettleWattage.value) {
      loadRequirements.push({
        id: this.electricKettleId,
        name: this.electricKettleTitle,
        number: isNaN(Number(this.electricKettleNumber?.value))
          ? null
          : Number(this.electricKettleNumber?.value),
        wattage: isNaN(Number(this.electricKettleWattage?.value))
          ? null
          : Number(this.electricKettleWattage?.value),
      });
    }

    if (
      this.energySavingBulbNumber.value ||
      this.energySavingBulbWattage.value
    ) {
      loadRequirements.push({
        id: this.energySavingBulbId,
        name: this.energySavingBulbTitle,
        number: isNaN(Number(this.energySavingBulbNumber?.value))
          ? null
          : Number(this.energySavingBulbNumber?.value),
        wattage: isNaN(Number(this.energySavingBulbWattage?.value))
          ? null
          : Number(this.energySavingBulbWattage?.value),
      });
    }

    if (this.flourescentTubeNumber.value || this.flourescentTubeWattage.value) {
      loadRequirements.push({
        id: this.flourescentTubeId,
        name: this.flourescentTubeTitle,
        number: isNaN(Number(this.flourescentTubeNumber?.value))
          ? null
          : Number(this.flourescentTubeNumber?.value),
        wattage: isNaN(Number(this.flourescentTubeWattage?.value))
          ? null
          : Number(this.flourescentTubeWattage?.value),
      });
    }

    if (this.homeTheatreNumber.value || this.homeTheatreWattage.value) {
      loadRequirements.push({
        id: this.homeTheatreId,
        name: this.homeTheatreTitle,
        number: isNaN(Number(this.homeTheatreNumber?.value))
          ? null
          : Number(this.homeTheatreNumber?.value),
        wattage: isNaN(Number(this.homeTheatreWattage?.value))
          ? null
          : Number(this.homeTheatreWattage?.value),
      });
    }

    if (
      this.incandescentBulbNumber.value ||
      this.incandescentBulbWattage.value
    ) {
      loadRequirements.push({
        id: this.incandescentBulbId,
        name: this.incandescentBulbTitle,
        number: isNaN(Number(this.incandescentBulbNumber?.value))
          ? null
          : Number(this.incandescentBulbNumber?.value),
        wattage: isNaN(Number(this.incandescentBulbWattage?.value))
          ? null
          : Number(this.incandescentBulbWattage?.value),
      });
    }

    if (this.laptopNumber.value || this.laptopWattage.value) {
      loadRequirements.push({
        id: this.laptopId,
        name: this.laptopTitle,
        number: isNaN(Number(this.laptopNumber?.value))
          ? null
          : Number(this.laptopNumber?.value),
        wattage: isNaN(Number(this.laptopWattage?.value))
          ? null
          : Number(this.laptopWattage?.value),
      });
    }

    if (this.ledBulbNumber.value || this.ledBulbWattage.value) {
      loadRequirements.push({
        id: this.ledBulbId,
        name: this.ledBulbTitle,
        number: isNaN(Number(this.ledBulbNumber?.value))
          ? null
          : Number(this.ledBulbNumber?.value),
        wattage: isNaN(Number(this.ledBulbWattage?.value))
          ? null
          : Number(this.ledBulbWattage?.value),
      });
    }

    if (this.microwaveOvenNumber.value || this.microwaveOvenWattage.value) {
      loadRequirements.push({
        id: this.microwaveOvenId,
        name: this.microwaveOvenTitle,
        number: isNaN(Number(this.microwaveOvenNumber?.value))
          ? null
          : Number(this.microwaveOvenNumber?.value),
        wattage: isNaN(Number(this.microwaveOvenWattage?.value))
          ? null
          : Number(this.microwaveOvenWattage?.value),
      });
    }

    if (this.printerNumber.value || this.printerWattage.value) {
      loadRequirements.push({
        id: this.printerId,
        name: this.printerTitle,
        number: isNaN(Number(this.printerNumber?.value))
          ? null
          : Number(this.printerNumber?.value),
        wattage: isNaN(Number(this.printerWattage?.value))
          ? null
          : Number(this.printerWattage?.value),
      });
    }

    if (this.refrigeratorNumber.value || this.refrigeratorWattage.value) {
      loadRequirements.push({
        id: this.refrigeratorId,
        name: this.refrigeratorTitle,
        number: isNaN(Number(this.refrigeratorNumber?.value))
          ? null
          : Number(this.refrigeratorNumber?.value),
        wattage: isNaN(Number(this.refrigeratorWattage?.value))
          ? null
          : Number(this.refrigeratorWattage?.value),
      });
    }

    if (this.standingFanNumber.value || this.standingFanWattage.value) {
      loadRequirements.push({
        id: this.standingFanId,
        name: this.standingFanTitle,
        number: isNaN(Number(this.standingFanNumber?.value))
          ? null
          : Number(this.standingFanNumber?.value),
        wattage: isNaN(Number(this.standingFanWattage?.value))
          ? null
          : Number(this.standingFanWattage?.value),
      });
    }

    if (this.televisionNumber.value || this.televisionWattage.value) {
      loadRequirements.push({
        id: this.televisionId,
        name: this.televisionTitle,
        number: isNaN(Number(this.televisionNumber?.value))
          ? null
          : Number(this.televisionNumber?.value),
        wattage: isNaN(Number(this.televisionWattage?.value))
          ? null
          : Number(this.televisionWattage?.value),
      });
    }

    if (this.toasterNumber.value || this.toasterWattage.value) {
      loadRequirements.push({
        id: this.toasterId,
        name: this.toasterTitle,
        number: isNaN(Number(this.toasterNumber?.value))
          ? null
          : Number(this.toasterNumber?.value),
        wattage: isNaN(Number(this.toasterWattage?.value))
          ? null
          : Number(this.toasterWattage?.value),
      });
    }

    if (this.vacuumCleanerNumber.value || this.vacuumCleanerWattage.value) {
      loadRequirements.push({
        id: this.vacuumCleanerId,
        name: this.vacuumCleanerTitle,
        number: isNaN(Number(this.vacuumCleanerNumber?.value))
          ? null
          : Number(this.vacuumCleanerNumber?.value),
        wattage: isNaN(Number(this.vacuumCleanerWattage?.value))
          ? null
          : Number(this.vacuumCleanerWattage?.value),
      });
    }

    if (this.washingMachineNumber.value || this.washingMachineWattage.value) {
      loadRequirements.push({
        id: this.washingMachineId,
        name: this.washingMachineTitle,
        number: isNaN(Number(this.washingMachineNumber?.value))
          ? null
          : Number(this.washingMachineNumber?.value),
        wattage: isNaN(Number(this.washingMachineWattage?.value))
          ? null
          : Number(this.washingMachineWattage?.value),
      });
    }

    if (this.waterHeaterNumber.value || this.waterHeaterWattage.value) {
      loadRequirements.push({
        id: this.waterHeaterId,
        name: this.waterHeaterTitle,
        number: isNaN(Number(this.waterHeaterNumber?.value))
          ? null
          : Number(this.waterHeaterNumber?.value),
        wattage: isNaN(Number(this.waterHeaterWattage?.value))
          ? null
          : Number(this.waterHeaterWattage?.value),
      });
    }

    if (this.waterPump05hpNumber.value || this.waterPump05hpWattage.value) {
      loadRequirements.push({
        id: this.waterPump05hpId,
        name: this.waterPump05hpTitle,
        number: isNaN(Number(this.waterPump05hpNumber?.value))
          ? null
          : Number(this.waterPump05hpNumber?.value),
        wattage: isNaN(Number(this.waterPump05hpWattage?.value))
          ? null
          : Number(this.waterPump05hpWattage?.value),
      });
    }

    if (this.waterPump1hpNumber.value || this.waterPump1hpWattage.value) {
      loadRequirements.push({
        id: this.waterPump1hpId,
        name: this.waterPump1hpTitle,
        number: isNaN(Number(this.waterPump1hpNumber?.value))
          ? null
          : Number(this.waterPump1hpNumber?.value),
        wattage: isNaN(Number(this.waterPump1hpWattage?.value))
          ? null
          : Number(this.waterPump1hpWattage?.value),
      });
    }

    return loadRequirements;
  }

  setServiceStateFromServer(model: KycAccountData) {
    if (this.isNavigatingToMeterRequest) {
      this.isNavigatingToMeterRequest = false;
      return;
    }

    if (model.currentPage == 9) {
      this.router.navigate(['/customers-app/account-information']);
      return;
    } else if (model.currentPage == 10) {
      this.router.navigate(['/customers-app']);
    } else if (model.currentPage == 11) {
      this.router.navigate(['/customers-app/update-meter-request-review']);
    } else if (model.currentPage == 12) {
      this.router.navigate(['/customers-app/review-application']);
    } else if (model.currentPage >= 13) {
      if (!model.rejections || model.rejections.length === 0) {
        this.router.navigate(['/customers-app/profile-incomplete']);
      }
      // If rejections exist, do not redirect — fall through to set step to first rejection step below
    }

    // Personal state
    this.id = model.id;
    if (model.rejections && model.rejections.length > 0) {
      // Rejection editing mode: start at the first step that has a rejection
      const firstStep = this.getFirstRejectionFormStep(model.rejections);
      this.step = firstStep ?? 1;
    } else {
      this.step = model.currentPage == 10 ? 4 : model.currentPage;
    }
    this.setPersonalDataTitle();
    this.customerId = model.customerId;
    this.meterRequest = model.meterRequest ?? null;
    // 1. step props
    this.selectedTitle = this.setTitle(model.title);
    this.surname.setValue(model.surname ?? '');
    this.firstName.setValue(model.firstName ?? '');
    this.phone.setValue(model.phoneNumber ?? '');
    this.dialCode = model.dialCode;
    this.email.setValue(model.email ?? '');
    this.dateOfBirth.setValue(new Date(model.dateOfBirth ?? ''));
    this.identificationIssueDate.setValue(
      new Date(model.identificationIssueDate ?? ''),
    );
    this.identificationExpiryDate.setValue(
      new Date(model.identificationExpiryDate ?? ''),
    );
    this.employerName.setValue(model.nameOfEmployer ?? '');
    this.houseNo.setValue(model.employerAddress?.houseNo ?? '');
    this.streetName.setValue(model.employerAddress?.street ?? '');
    this.landmark.setValue(model.employerAddress?.landmark ?? '');
    this.city.setValue(model.employerAddress?.city ?? '');
    this.area.setValue(model.employerAddress?.area ?? '');
    this.middleName.setValue(model.middleName ?? '');
    this.selectedGender = this.setGender(model.gender);
    this.selectedNationality = this.setNationality(
      model.nationality?.toString(),
    );
    this.selectedOwnershipStatus = this.setOwnershipStatus(
      model.ownershipStatus?.toString(),
    );
    this.selectedClientType =
      this.clientTypes.find((el) => el.value === model.clientType) ?? null;
    this.selectedCustomerType =
      this.customerTypes.find((el) => el.value === model.customerType) ?? null;
    this.selectedIdentificationOfLandlord = this.setIdentification(
      model.identificationOfLandlord,
      model.clientType,
    );
    // this.selectedClientType = model.clientType ? { displayValue: model.clientType, value: model.clientType } : null;
    // 2. step props
    this.accountNumber.setValue(model.accountNumber ?? '');
    this.meterNumber.setValue(model.meterNumberLandlord ?? '');
    this.bankVerificationNumber.setValue(model.bankVerificationNumber ?? '');
    this.numberOfPremises.setValue(
      model.accountNumberForPremissesLandlord ?? '',
    );
    this.identificationValue = model.identificationOfLandlord;
    this.selectedServiceType = this.setServiceType(
      model.serviceType?.toString(),
    );
    this.selectedSubServiceType = this.setSubServiceType(
      model.subServiceType?.toString(),
    );
    this.personnelIDNumOfLandlord.setValue(
      model.personalIdNumberOfLandlord ?? '',
    );
    this.supplyHouseNo.setValue(model.addressOfLandlord?.houseNo ?? '');
    this.supplyStreetName.setValue(model.addressOfLandlord?.street ?? '');
    this.supplyLandmark.setValue(model.addressOfLandlord?.landmark ?? '');
    this.geoLatitude = model.latitude ?? null;
    this.geoLongitude = model.longitude ?? null;
    this.geoLocation.setValue(
      this.formatGeoLocation(this.geoLatitude, this.geoLongitude),
    );
    this.supplyCity.setValue(model.addressOfLandlord?.city ?? '');
    this.supplyArea.setValue(model.addressOfLandlord?.area ?? '');
    this.selectedTypeOfPermises = this.setTypeOfPermises(model.typeOfPremises);
    this.selectedUseOfPermises = this.setUseOfPermises(model.useOfPremises);
    // this.buildingDevelopmentStages = this.setBuildingDevelopmentStage(model.buildingDevelopmentStage);
    this.selectedBuildingDevelopmentStage = this.setBuildingDevelopmentStage(
      model.buildingDevelopmentStage?.toString(),
    );
    this.activity = model.activity
      ? { displayValue: model.activity, value: model.activity }
      : null;
    this.subActivity = model.subActivity
      ? { displayValue: model.subActivity, value: model.subActivity }
      : null;
    this.selectedPremiseType = model.premiseType
      ? { displayValue: model.premiseType, value: model.premiseType }
      : null;
    this.selectedPremiseCategory = model.premiseCategory
      ? { displayValue: model.premiseCategory, value: model.premiseCategory }
      : null;
    this.selectedUseOfPremise = this.setUseOfPremise(
      model.useOfPremise?.toString(),
    );

    if (this.hasCimIntegrationEnabled) {
      this.landlordAddressSubGeoRegionId =
        model.addressOfLandlord?.subGeographicalRegionId;
      this.supplySubGeoRegionId =
        model.addressOfLandlord?.subGeographicalRegionId;
    } else {
      this.landlordAddressAreaId = model.addressOfLandlord?.areaId;
      this.supplyAreaId = model.addressOfLandlord.areaId;
    }

    this.sltStatusId = model.sltStatusId;
    // 3. step props
    this.previousMeterNumber.setValue(model.meterNumberPreviousCustomer ?? '');
    this.nin.setValue(model.nin ?? '');
    this.previousAccountNumber.setValue(
      model.accountNumberPreviousCustomer ?? '',
    );
    // 4. step props

    this.personalIDNumber.setValue(model.personalIdNumberOfBuildingOwner ?? '');
    this.buildingOwner.setValue(model.buildingOwner ?? '');
    // 5. step props
    this.setFifthStepFromServer(model.loadRequirements ?? []);

    // 6. step props
    this.selectedNatureOfProperty = this.setNatureOfProperty(
      model.propertyNature,
    );
    this.selectedtypeOfProperty = this.setTypeOfProperty(model.typeOfProperty);
    this.propertyHavePlanningPermission = model.hasPlanningPermissions ?? false;
    this.propertyHaveMeter = model.hasMeter ?? false;
    this.meterNumber6.setValue(model.meterNumber ?? '');
    this.customerAccountNumberForPremises.setValue(
      model.buildingOwnerAccountNumber ?? '',
    );
    this.estMaxLoad.setValue(model.estimatedMaxLoad);
    this.requestedVoltage.setValue(model.requestedVoltage);
    if (model.nameOfElectricalEngineer) {
      this.engineerNameDesktop.setValue(model.nameOfElectricalEngineer ?? '');
      this.engineerName.setValue(model.nameOfElectricalEngineer.split(' ')[0]);
      this.engineerLastName.setValue(
        model.nameOfElectricalEngineer.split(' ').slice(1).join(' '),
      );
    }

    // 7. step props
    this.engineerHouseNo.setValue(
      model.addressElectricalEngineer?.houseNo ?? '',
    );
    this.engineerStreet.setValue(model.addressElectricalEngineer?.street ?? '');
    this.engineerLandmark.setValue(
      model.addressElectricalEngineer?.landmark ?? '',
    );
    this.engineerCity.setValue(model.addressElectricalEngineer?.city ?? '');
    this.engineerArea.setValue(model.addressElectricalEngineer?.area ?? '');
    this.licenseNumber.setValue(model.licenseNumber ?? '');
    this.category.setValue(model.category ?? '');
    this.seventhStepDate.setValue(new Date(model.engineerDate ?? ''));
    this.engineerMeterNumber.setValue(
      model.meterNumberRequestingForChange ?? '',
    );
    this.customerAccountNumber.setValue(
      model.accountNumberRequestingForChange ?? '',
    );
    // 8. step props
    this.customersSignitureUrl = model.customersSignitureUrl;
    this.landlordsSignitureUrl = model.landlordsSignitureUrl;
    this.electricalContractorsSignitureUrl =
      model.electricalContractorsSignitureUrl;
    this.applicantsSignitureUrl = model.applicantsSignitureUrl;
    this.passportUrl = model.passportUrl;
    this.customersSignitureName = model.customersSignitureName ?? '';
    this.landlordsSignitureName = model.landlordsSignitureName ?? '';
    this.electricalContractorsSignitureName =
      model.electricalContractorsSignitureName ?? '';
    this.applicantsSignitureName = model.applicantsSignitureName ?? '';
    this.passportName = model.passportName ?? '';
    this.isAgreed = model.debtAmountAcceptance ?? false;
    this.rejections = model.rejections;
    this.proofOfOwnershipUrl = model.proofOfOwnershipUrl;
    this.letterOfConsentUrl = model.letterOfConsentUrl;
    this.tenancyAgreementUrl = model.tenancyAgreementUrl;
    this.letterOfAuthorizationUrl = model.letterOfAuthorizationUrl;

    this.proofOfOwnershipName = model.proofOfOwnershipName ?? '';
    this.letterOfConsentName = model.letterOfConsentName ?? '';
    this.letterOfAuthorizationName = model.letterOfAuthorizationName ?? '';
    this.tenancyAgreementName = model.tenancyAgreementName ?? '';
  }

  setKycAccountDataForServer(): KycAccountData {
    // If value is a string then it is set from server, otherwise user has changed the value manually in phone input
    const isPhoneNumberStringOrNull =
      typeof this.phone.value === 'string' ||
      this.phone.value === null ||
      this.phone.value === undefined;

    this.dialCode = isPhoneNumberStringOrNull
      ? this.dialCode
      : (this.phone.value?.dialCode.replace('+', '') ?? null);

    const model: KycAccountData = {
      id: this.id ?? 0,
      customerId: this.customerId,
      currentPage: this.step !== 0 ? this.step : 1,
      // 1. step props
      title: Number(this.selectedTitle?.value),
      surname: this.surname.value,
      firstName: this.firstName.value,
      phoneNumber: isPhoneNumberStringOrNull
        ? this.phone.value
        : this.phone.value?.number,
      dialCode: this.dialCode,
      email: this.email.value,
      gender:
        this.selectedGender?.value == 'Male'
          ? Gender.Male
          : this.selectedGender?.value == 'Female'
            ? Gender.Female
            : null,
      middleName: this.middleName.value ?? null,
      nationality: this.selectedNationality?.value ?? null,
      dateOfBirth: this.dateOfBirth.value,
      identificationIssueDate: this.identificationIssueDate.value,
      identificationExpiryDate: this.identificationExpiryDate.value,
      nameOfEmployer: this.employerName.value,
      ownershipStatus:
        this.selectedOwnershipStatus?.value != null
          ? Number(this.selectedOwnershipStatus.value)
          : null,
      customerType: Number(this.selectedCustomerType?.value) ?? null,
      clientType: Number(this.selectedClientType?.value) ?? null,

      // 2. step props
      meterNumberLandlord: this.meterNumber?.value ?? '',
      accountNumberForPremissesLandlord: this.numberOfPremises?.value ?? '',
      identificationOfLandlord: isNaN(
        Number(this.selectedIdentificationOfLandlord?.value),
      )
        ? null
        : Number(this.selectedIdentificationOfLandlord?.value),
      personalIdNumberOfLandlord: this.personnelIDNumOfLandlord?.value ?? '',
      addressOfLandlord: {
        houseNo: this.supplyHouseNo.value,
        street: this.supplyStreetName.value,
        landmark: this.supplyLandmark.value,
        city: this.supplyCity.value,
        area: this.supplyArea.value,
        areaId:
          !this.hasCimIntegrationEnabled && this.supplyAreaId
            ? this.supplyAreaId
            : null,
        subGeographicalRegionId:
          this.hasCimIntegrationEnabled && this.supplySubGeoRegionId
            ? this.supplySubGeoRegionId
            : null,
      },
      bankVerificationNumber: this.bankVerificationNumber?.value ?? '',
      latitude: this.geoLatitude,
      longitude: this.geoLongitude,
      typeOfPremises: isNaN(Number(this.selectedTypeOfPermises?.value))
        ? null
        : Number(this.selectedTypeOfPermises?.value),
      useOfPremises: isNaN(Number(this.selectedUseOfPermises?.value))
        ? null
        : Number(this.selectedUseOfPermises?.value),
      serviceType:
        this.selectedServiceType?.value != null
          ? Number(this.selectedServiceType.value)
          : null,
      subServiceType:
        this.selectedSubServiceType?.value != null
          ? Number(this.selectedSubServiceType.value)
          : null,
      useOfPremise:
        this.selectedUseOfPremise?.value != null
          ? Number(this.selectedUseOfPremise.value)
          : null,
      buildingDevelopmentStage:
        this.selectedBuildingDevelopmentStage?.value != null
          ? Number(this.selectedBuildingDevelopmentStage.value)
          : null,
      buildingOwner: this.buildingOwner.value,
      premiseType:
        this.selectedPremiseType?.value != null
          ? String(this.selectedPremiseType.value)
          : null,
      premiseCategory:
        this.selectedPremiseCategory?.value != null
          ? String(this.selectedPremiseCategory.value)
          : null,
      activity: this.activity?.value ? String(this.activity.value) : null,
      subActivity: this.subActivity?.value
        ? String(this.subActivity.value)
        : null,
      meterRequest: this.meterRequest ?? undefined,
      sltStatusId: this.sltStatusId ?? null,
      sltStatus: this.sltStatus,
      // 3. step props
      nin: this.nin.value,
      meterNumberPreviousCustomer: this.previousMeterNumber.value,
      accountNumberPreviousCustomer: this.previousAccountNumber.value,
      // 4. step props

      personalIdNumberOfBuildingOwner: this.personalIDNumber.value,

      // 5. step props
      loadRequirements: this.setFifthStepForServer(),

      // 6. step props
      propertyNature: isNaN(Number(this.selectedNatureOfProperty?.value))
        ? null
        : Number(this.selectedNatureOfProperty?.value),
      typeOfProperty: isNaN(Number(this.selectedtypeOfProperty?.value))
        ? null
        : Number(this.selectedtypeOfProperty?.value),
      hasPlanningPermissions: this.propertyHavePlanningPermission,
      hasMeter: this.propertyHaveMeter,
      meterNumber: this.meterNumber6.value,
      buildingOwnerAccountNumber:
        this.customerAccountNumberForPremises.value ?? '',
      estimatedMaxLoad: isNaN(Number(this.estMaxLoad.value))
        ? null
        : Number(this.estMaxLoad.value),
      requestedVoltage: isNaN(Number(this.requestedVoltage.value))
        ? null
        : Number(this.requestedVoltage.value),

      // 7. step props
      licenseNumber: this.licenseNumber.value,
      category: this.category.value,
      engineerDate: this.seventhStepDate.value,
      meterNumberRequestingForChange: this.engineerMeterNumber.value,
      accountNumberRequestingForChange: this.customerAccountNumber.value,

      // 8. step props // TODO:
      customersSignitureUrl: this.customersSignitureUrl,
      landlordsSignitureUrl: this.landlordsSignitureUrl,
      electricalContractorsSignitureUrl: this.electricalContractorsSignitureUrl,
      applicantsSignitureUrl: this.applicantsSignitureUrl,
      passportUrl: this.passportUrl,

      customersSignitureName: this.customersSignitureName,
      landlordsSignitureName: this.landlordsSignitureName,
      electricalContractorsSignitureName:
        this.electricalContractorsSignitureName,
      applicantsSignitureName: this.applicantsSignitureName,
      passportName: this.passportName,
      debtAmountAcceptance: this.isAgreed,
      rejections: this.rejections,
      profilePictureUrl: null,
      proofOfOwnershipUrl: this.proofOfOwnershipUrl,
      letterOfConsentUrl: this.letterOfConsentUrl,
      tenancyAgreementUrl: this.tenancyAgreementUrl,
      letterOfAuthorizationUrl: this.letterOfAuthorizationUrl,
      proofOfOwnershipName: this.proofOfOwnershipName,
      letterOfConsentName: this.letterOfConsentName,
      letterOfAuthorizationName: this.letterOfAuthorizationName,
      tenancyAgreementName: this.tenancyAgreementName,
      relatedAccounts: [],
    };

    if (this.engineerNameDesktop.value) {
      model.nameOfElectricalEngineer = this.engineerNameDesktop.value;
    } else if (this.engineerName.value || this.engineerLastName.value) {
      model.nameOfElectricalEngineer = `${this.engineerName.value} ${this.engineerLastName.value}`;
    }
    return model;
  }

  private formatGeoLocation(
    latitude?: number | null,
    longitude?: number | null,
  ): string {
    if (latitude != null && longitude != null) {
      return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
    }

    return '';
  }

  finish() {}
  handleGetRegionsForSelect(event: any) {
    this.regions = event.body.data.map(function (region: any) {
      return {
        value: region.id,
        displayValue: region.name,
      };
    });
    if (this.areas?.length > 0 && this.regions?.length > 0) {
      this.mapSelectedRegionAndAreas();
    }
  }
  handleGetAreasForSelect(event: any) {
    this.areas = event.body.data;
    this.shownAreas = event.body.data;
    // this.shownPreviousCustomerAreas = event.body.data;
    if (this.areas?.length > 0 && this.regions?.length > 0) {
      this.mapSelectedRegionAndAreas();
    }
  }

  handleGetGeoRegionsForSelect(event: any) {
    this.geographicalRegions = event.body.data.map(function (
      region: any,
    ) {
      return {
        value: region.id,
        displayValue: region.name,
      };
    });
    if (
      this.subGeoRegions?.length > 0 &&
      this.geographicalRegions?.length > 0
    ) {
      this.mapSelectedGeoAndSubGeoRegions();
    }
  }

  handleGetSubGeographicalRegionsForSelect(event: any) {
    this.subGeoRegions = event.body.data ?? [];
    this.shownSubGeoRegions = event.body.data ?? [];

    if (this.subGeoRegions.length > 0 && this.geographicalRegions?.length > 0) {
      this.mapSelectedGeoAndSubGeoRegions();
    }
  }

  handleActivities(event: any) {
    // this.
  }

  mapSelectedRegionAndAreas() {
    if (this.areaId != 0) {
      this.selectedArea = this.areas.find((x) => x.id === this.areaId)!;
      this.selectedRegion = this.regions.find(
        (x) => x.value === this.selectedArea?.regionId,
      )!;
    }
    if (this.landlordAddressAreaId != 0) {
      this.reviewAppArea = this.areas.find(
        (x) => x.id === this.landlordAddressAreaId,
      )!;
      this.reviewAppRegion = this.regions.find(
        (x) => x.value === this.reviewAppArea?.regionId,
      )!;
    }

    if (this.supplyAreaId != 0) {
      this.selectedSupplyArea = this.areas.find(
        (x) => x.id === this.supplyAreaId,
      )!;
      this.selectedSupplyRegion = this.regions.find(
        (x) => x.value === this.selectedSupplyArea?.regionId,
      )!;
    }
  }

  mapSelectedGeoAndSubGeoRegions() {
    if (this.subGeoRegionId != 0) {
      this.selectedSubGeoRegion = this.subGeoRegions.find(
        (x) => x.id === this.subGeoRegionId,
      )!;
      this.selectedGeoRegion = this.geographicalRegions.find(
        (x) => x.value === this.selectedSubGeoRegion?.geographicalRegion.id,
      )!;
    }
    if (this.landlordAddressSubGeoRegionId != 0) {
      this.reviewAppSubGeoRegion = this.subGeoRegions.find(
        (x) => x.id === this.landlordAddressSubGeoRegionId,
      )!;
      this.reviewAppGeoRegion = this.geographicalRegions.find(
        (x) => x.value === this.reviewAppSubGeoRegion?.geographicalRegion.id,
      )!;
    }

    if (this.supplySubGeoRegionId != 0) {
      this.selectedSupplySubGeoRegion = this.subGeoRegions.find(
        (x) => x.id === this.supplySubGeoRegionId,
      )!;
      this.selectedSupplyGeoRegion = this.geographicalRegions.find(
        (x) =>
          x.value === this.selectedSupplySubGeoRegion?.geographicalRegion.id,
      )!;
    }

    if (this.landlordAddressSubGeoRegionId != 0) {
      this.reviewAppSubGeoRegion = this.subGeoRegions.find(
        (x) => x.id === this.landlordAddressSubGeoRegionId,
      )!;
      this.reviewAppGeoRegion = this.geographicalRegions.find(
        (x) => x.value === this.reviewAppSubGeoRegion?.geographicalRegion.id,
      )!;
    }

    if (this.supplySubGeoRegionId != 0) {
      this.selectedSupplySubGeoRegion = this.subGeoRegions.find(
        (x) => x.id === this.supplySubGeoRegionId,
      )!;
      this.selectedSupplyGeoRegion = this.geographicalRegions.find(
        (x) =>
          x.value === this.selectedSupplySubGeoRegion?.geographicalRegion.id,
      )!;
    }
  }

  initAreaState() {
    if (this.hasCimIntegrationEnabled) {
      this.geographicalRegionService.getAllForSelect().subscribe({
        next: this.handleGetGeoRegionsForSelect.bind(this),
        error: this.handleError.bind(this),
      });

      this.subGeographicalRegionService.getAllForMappings().subscribe({
        next: this.handleGetSubGeographicalRegionsForSelect.bind(this),
        error: this.handleError.bind(this),
      });
    } else {
      this.regionService.getAllForSelectNoUser().subscribe({
        next: this.handleGetRegionsForSelect.bind(this),
        error: this.handleError.bind(this),
      });
      this.areaService.getAllForOptions().subscribe({
        next: this.handleGetAreasForSelect.bind(this),
        error: this.handleError.bind(this),
      });
    }
  }
  initState() {
    this.kycCustomerApiService.getCustomer().subscribe({
      next: this.handleGetKycAccountData.bind(this),
      error: this.handleError.bind(this),
    });
    this.hasCimIntegrationEnabled =
      localStorage.getItem('cimIntegrationEnabled') === 'true';
    this.initAreaState();
    this.customerService.getActivities().subscribe({
      next: this.handleActivities.bind(this),
      error: this.handleError.bind(this),
    });
  }
}
